import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, SelectChangeEvent, TextField, Tooltip, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Address from '@/components/common/address_components/Address';
import PhoneNumberSelect from '@/components/common/contact_components/PhoneNumberSelect';
import { SelectInput } from '@/components/common/select_input/SelectInput';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { AddressProps } from '@/models/Address';
import { ContactProps, ContactType } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { isEmail } from '@/utils/emailUtil';

import CharacterLimitHelperText from '../CharacterLimitHelperText';
import { Email } from '../text_input/Email';

const defaultContactType: ContactType = {
  displayEmail: true,
  displayPhone: true,
};

export function PartnerContact({
  id,
  contact: contactInfo,
  contactType = defaultContactType,
  emailDisabled = false,
  showErrors = true,
  defaultCountryId = 56,
  fieldHelperText,
  onChange,
}: {
  id: string;
  contact: ContactProps | null;
  contactType?: ContactType;
  emailDisabled?: boolean;
  showErrors?: boolean;
  defaultCountryId?: number | null;
  fieldHelperText?: Partial<Record<keyof ContactProps, string>>;
  onChange: (contact: ContactProps) => void;
}) {
  const theme = useTheme();
  const { allCountries } = useContext(ConfigContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { translatedText } = useContext(LocaleContext);
  const [countryCode, setCountryCode] = useState('US');
  const safeContactInfo = useMemo<ContactProps>(() => {
    return {
      contactRelationships: [],
      ...(contactInfo ?? {}),
    };
  }, [contactInfo]);

  const getFieldHelperText = useCallback(
    (field: keyof ContactProps): string => fieldHelperText?.[field]?.trim() ?? '',
    [fieldHelperText]
  );

  const getLabelWithTooltipIcon = useCallback(
    (field: keyof ContactProps, label: ReactNode): ReactNode => {
      const helperText = getFieldHelperText(field);
      const hasTooltip = helperText !== '';

      if (!hasTooltip) {
        return label;
      }

      return (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box component='span'>{label}</Box>
          {hasTooltip && (
            <Tooltip title={helperText} placement='top' arrow>
              <InfoOutlinedIcon
                sx={{
                  fontSize: '1rem',
                  color: theme.palette.info.main,
                  verticalAlign: 'middle',
                }}
              />
            </Tooltip>
          )}
        </Box>
      );
    },
    [getFieldHelperText, theme.palette.info.main]
  );

  function isNameValid(name: string): boolean {
    return name != null && name.length >= 2;
  }

  function updatePhoneNumber(value: string) {
    onChange({
      ...safeContactInfo,
      phone: value,
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange({
      ...safeContactInfo,
      [e.target.id]: e.target.value,
    });
  }

  useEffect(() => {
    if (allCountries != null) {
      const country = allCountries.find((c) => c.id === defaultCountryId);
      setCountryCode(country?.abbrev != null ? country.abbrev : 'US');
    }
  }, [allCountries, defaultCountryId]);

  const contactAddress: AddressProps = useMemo(() => {
    return {
      city: contactInfo?.city,
      countryId: contactInfo?.countryId ?? defaultCountryId,
      countyId: contactInfo?.countyId,
      stateId: contactInfo?.stateId,
      streetAddress1: contactInfo?.streetAddress1,
      streetAddress2: contactInfo?.streetAddress2,
      zipCode: contactInfo?.zipCode,
    };
  }, [contactInfo, defaultCountryId]);

  const selectedDepartment = useMemo(() => {
    return contactType?.departmentTypes?.find((item) => item.id === contactInfo?.departmentTypeId);
  }, [contactInfo?.departmentTypeId, contactType]);

  const isOtherDepartment = useMemo(() => {
    return selectedDepartment?.name?.toLowerCase() === 'other';
  }, [selectedDepartment]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      onChange({
        ...safeContactInfo,
        title: newTitle,
      });
    },
    [onChange, safeContactInfo]
  );

  const handleAddressUpdate = useCallback(
    (address: AddressProps) => {
      onChange({
        ...safeContactInfo,
        streetAddress1: address.streetAddress1,
        streetAddress2: address.streetAddress2,
        city: address.city,
        countyId: address.countyId,
        stateId: address.stateId,
        countryId: address.countryId,
        zipCode: address.zipCode,
      });
    },
    [onChange, safeContactInfo]
  );

  return (
    <Box id={id}>
      <Grid container rowSpacing={2} columnSpacing={4} mb={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            variant='outlined'
            fullWidth
            value={contactInfo?.firstName != null ? contactInfo.firstName : ''}
            onChange={handleChange}
            label={getLabelWithTooltipIcon('firstName', translatedText.contact.labelFirstName)}
            id={'firstName'}
            error={
              showErrors && (contactInfo?.firstName == null || !isNameValid(contactInfo?.firstName))
            }
            inputProps={{ maxLength: 500 }}
            required
          />
          {contactInfo?.firstName != null && contactInfo.firstName.length >= 500 && (
            <CharacterLimitHelperText limit={500} />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            variant='outlined'
            fullWidth
            value={contactInfo?.lastName != null ? contactInfo.lastName : ''}
            onChange={handleChange}
            label={getLabelWithTooltipIcon('lastName', translatedText.contact.labelLastNameLabel)}
            id={'lastName'}
            error={
              showErrors && (contactInfo?.lastName == null || !isNameValid(contactInfo?.lastName))
            }
            inputProps={{ maxLength: 500 }}
            required
          />

          {contactInfo?.lastName != null && contactInfo.lastName.length >= 500 && (
            <CharacterLimitHelperText limit={500} />
          )}
        </Grid>
        {contactType.displayPhone && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <PhoneNumberSelect
              id='phone'
              label={getLabelWithTooltipIcon('phone', translatedText.contact.labelPhoneNumber)}
              phone={contactInfo?.phone}
              updatePhoneNumber={updatePhoneNumber}
              showError={showErrors}
              defaultCountry={countryCode}
              required
            />
          </Grid>
        )}

        {contactType.displayEmail && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Email
              variant='outlined'
              fullWidth
              id='email'
              label={getLabelWithTooltipIcon('email', translatedText.contact.labelEmail)}
              disabled={emailDisabled}
              sx={{ flexGrow: 1 }}
              value={contactInfo?.email != null ? contactInfo.email : ''}
              required
              error={
                showErrors &&
                (contactInfo?.email == null ||
                  (contactInfo.email !== '' && !isEmail(contactInfo.email)))
              }
              onChange={handleChange}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
        )}

        {selectedProgramId !== ProgramNames.TreeCampusK12 && contactType.displayTitle && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              variant='outlined'
              fullWidth
              onChange={handleChange}
              label={getLabelWithTooltipIcon('title', translatedText.contact.labelTitle)}
              id={'title'}
              value={contactInfo?.title != null ? contactInfo.title : ''}
              error={showErrors && (contactInfo?.title == null || !isNameValid(contactInfo?.title))}
              helperText={
                showErrors && contactInfo?.title != null && !isNameValid(contactInfo?.title)
                  ? translatedText.myaccount.titlerequired
                  : ''
              }
              inputProps={{ maxLength: 50 }}
              required
            />

            {contactInfo?.title != null && 50 - contactInfo.title.length == 0 && (
              <CharacterLimitHelperText limit={50} />
            )}
          </Grid>
        )}

        {contactType.displayDepartment && (
          <Grid size={{ xs: 12, sm: 6 }} display='flex' flexDirection='column' gap={2}>
            <SelectInput
              id={'departmentContactId'}
              label={getLabelWithTooltipIcon('departmentTypeId', 'Home Department')}
              hasError={showErrors && contactInfo?.departmentTypeId == null}
              disabled={
                contactType?.departmentTypes == null ||
                !Array.isArray(contactType.departmentTypes) ||
                contactType.departmentTypes.length == 0
              }
              required
              value={
                contactInfo?.departmentTypeId != null ? contactInfo.departmentTypeId.toString() : ''
              }
              onChange={(e: SelectChangeEvent) => {
                onChange({
                  ...safeContactInfo,
                  departmentTypeId: Number(e.target.value),
                });
              }}
              selectOptions={
                contactType?.departmentTypes != null
                  ? contactType.departmentTypes.map((item) => {
                      return {
                        value: item.id.toString(),
                        label: item.name,
                      };
                    })
                  : []
              }
            />

            {isOtherDepartment && (
              <TextField
                variant='outlined'
                onChange={handleChange}
                required
                value={contactInfo?.departmentOther ?? ''}
                label={getLabelWithTooltipIcon(
                  'departmentOther',
                  translatedText.signup.labelSubCategoryOther
                )}
                id='departmentOther'
                error={
                  showErrors &&
                  (contactInfo?.departmentOther == null || contactInfo?.departmentOther == '')
                }
              />
            )}
          </Grid>
        )}

        {selectedProgramId == ProgramNames.TreeCampusK12 && contactType.displayTitle && (
          <>
            <Grid display={'flex'} size={{ xs: 12, sm: 6 }}>
              <SelectInput
                id={'title-select'}
                label={getLabelWithTooltipIcon(
                  'contactSubTypeId',
                  translatedText.signup.labelTitle
                )}
                value={
                  contactInfo?.contactSubTypeId != null
                    ? contactInfo.contactSubTypeId.toString()
                    : ''
                }
                hasError={showErrors && contactInfo?.contactSubTypeId == null}
                required
                onChange={(e: SelectChangeEvent) => {
                  e.preventDefault();
                  e.stopPropagation();

                  window.setTimeout(function () {
                    let lookup: { [key: number]: string | null } = {
                      1: null,
                      2: 'Teacher',
                      3: 'Principal (or equivalent)',
                      4: 'Assistant Principal (or equivalent)',
                      5: null,
                    };

                    const contactSubTypeId: number = Number(e.target.value);

                    if (isNaN(contactSubTypeId)) {
                      return;
                    }

                    lookup = {
                      1: null,
                      2: 'Teacher',
                      3: 'Principal (or equivalent)',
                      4: 'Assistant Principal (or equivalent)',
                      5: null,
                    };

                    onChange({
                      ...safeContactInfo,
                      contactSubTypeId: contactSubTypeId,
                      title: lookup[contactSubTypeId],
                    });
                  }, 1000);
                }}
                selectOptions={[
                  { value: '2', label: 'Teacher' },
                  { value: '3', label: 'Principal (or equivalent)' },
                  {
                    value: '4',
                    label: 'Assistant Principal (or equivalent)',
                  },
                  { value: '5', label: 'Other' },
                ]}
              />
            </Grid>
            <Grid display={'flex'} size={{ xs: 12, sm: 6 }}>
              {contactInfo?.contactSubTypeId == 5 && (
                <TextField
                  variant='outlined'
                  fullWidth
                  onChange={handleTitleChange}
                  value={contactInfo?.title != null ? contactInfo?.title : ''}
                  label={getLabelWithTooltipIcon(
                    'title',
                    translatedText.signup.labelSubCategoryOther
                  )}
                  id='titleOther'
                  inputProps={{ maxLength: 50 }}
                />
              )}
              {contactInfo?.title != null && 50 - contactInfo.title.length <= 0 && (
                <CharacterLimitHelperText limit={50} />
              )}
            </Grid>
          </>
        )}
      </Grid>

      <Address
        id={`${id}-contact-address`}
        address={contactAddress}
        onChange={handleAddressUpdate}
        showErrors={showErrors}
      />
    </Box>
  );
}
